import { CorrespondenceTeamDto } from '@cradua/data';
import { createReducer, on, Action } from '@ngrx/store';
import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity';

import * as CorrespondenceTeamActions from './team.actions';

export const CORRESPONDENCE_TEAM_FEATURE_KEY = 'correspondenceTeam';

export interface CorrespondenceTeamState
  extends EntityState<CorrespondenceTeamDto> {
  selectedCorrespondenceTeamId?: string | number; // which CorrespondenceTeam record has been selected
  loaded: boolean; // has the CorrespondenceTeam list been loaded
  error?: string | null; // last none error (if any)
}

export interface CorrespondenceTeamPartialState {
  readonly [CORRESPONDENCE_TEAM_FEATURE_KEY]: CorrespondenceTeamState;
}

export const correspondenceTeamAdapter: EntityAdapter<CorrespondenceTeamDto> = createEntityAdapter<
  CorrespondenceTeamDto
>({
  selectId: (correspondenceTeam) => correspondenceTeam.id,
  sortComparer: false,
});

export const initialState: CorrespondenceTeamState = correspondenceTeamAdapter.getInitialState(
  {
    // set initial required properties
    loaded: false,
    error: null,
    selectedCorrespondenceTeamId: null,
  }
);

const correspondenceTeamReducer = createReducer(
  initialState,
  on(CorrespondenceTeamActions.loadCorrespondenceTeam, (state) => ({
    ...state,
    loaded: true,
    error: null,
  })),
  on(
    CorrespondenceTeamActions.loadCorrespondenceTeamSuccess,
    (state, { correspondenceTeam }) =>
      correspondenceTeamAdapter.addAll(correspondenceTeam, {
        ...state,
        loaded: false,
      })
  ),
  on(
    CorrespondenceTeamActions.loadCorrespondenceTeamFailure,
    (state, { error }) => ({
      ...state,
      loaded: false,
      error,
    })
  ),
  on(CorrespondenceTeamActions.addCorrespondenceTeam, (state) => ({
    ...state,
    loaded: true,
    error: null,
  })),
  on(
    CorrespondenceTeamActions.addCorrespondenceTeamSuccess,
    (state, { newCorrespondenceTeam }) =>
      correspondenceTeamAdapter.addMany(newCorrespondenceTeam, {
        ...state,
        loaded: false,
      })
  ),
  on(
    CorrespondenceTeamActions.addCorrespondenceTeamFailure,
    (state, { error }) => ({
      ...state,
      loaded: false,
      error,
    })
  ),
  on(CorrespondenceTeamActions.updateCorrespondenceTeam, (state) => ({
    ...state,
    loaded: true,
    error: null,
  })),
  on(
    CorrespondenceTeamActions.updateCorrespondenceTeamSuccess,
    (state, { updateCorrespondenceTeam }) =>
      correspondenceTeamAdapter.upsertMany(updateCorrespondenceTeam, {
        ...state,
        loaded: false,
      })
  ),
  on(
    CorrespondenceTeamActions.updateCorrespondenceTeamFailure,
    (state, { error }) => ({
      ...state,
      loaded: false,
      error,
    })
  ),
  on(CorrespondenceTeamActions.removeCorrespondenceTeam, (state) => ({
    ...state,
    loaded: true,
    error: null,
  })),
  on(
    CorrespondenceTeamActions.removeCorrespondenceTeamSuccess,
    (state, { ids }) =>
      correspondenceTeamAdapter.removeMany(ids, { ...state, loaded: false })
  ),
  on(
    CorrespondenceTeamActions.removeCorrespondenceTeamFailure,
    (state, { error }) => ({
      ...state,
      loaded: false,
      error,
    })
  )
);

export function reducer(
  state: CorrespondenceTeamState | undefined,
  action: Action
) {
  return correspondenceTeamReducer(state, action);
}
